import React, { useState, useEffect } from "react";
import { agentService } from "../../services/agentService";
import { materialService } from "../../services/materialService";
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  TrashIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { binService } from "../../services/binService";
import ChatModal from "../Chat/ChatModal";
import RedirectOrderModal from "./RedirectOrderModal";
import CallButton from "../Call/CallButton";
import StartMeetingButton from "../Meeting/StartMeetingButton";
import { useAuth } from "../../contexts/AuthContext";

const PendingValidations = () => {
  const { user } = useAuth();
  const [pendingValidations, setPendingValidations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState({});
  const [message, setMessage] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [chatBinId, setChatBinId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [validationsData, materialsData] = await Promise.all([
        agentService.getPendingValidations(),
        materialService.getAllMaterials(),
      ]);
      setPendingValidations(validationsData);
      setMaterials(materialsData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setMessage("Error loading pending validations");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (binId, validationData) => {
    setValidating((prev) => ({ ...prev, [binId]: true }));
    setMessage("");

    try {
      await agentService.validateBin(binId, validationData);
      await fetchData(); // Refresh data
      setMessage("Bin validated successfully!");
    } catch (error) {
      console.error("Error validating bin:", error);
      setMessage(error.response?.data?.message || "Error validating bin");
    } finally {
      setValidating((prev) => ({ ...prev, [binId]: false }));
    }
  };

  const handleChatClick = (binId) => {
    setChatBinId(binId);
    setShowChat(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Pending Validations
        </h1>
        <p className="text-gray-600">
          Review and validate bins from sellers. Set accurate quantities and
          ensure quality standards.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Pending Validations List */}
      {pendingValidations?.bins?.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No pending validations
          </h2>
          <p className="text-gray-600">
            All bins have been validated or there are no new submissions.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {pendingValidations?.data?.bins?.map((validation) => (
            <ValidationCard
              key={validation._id}
              fetchData={fetchData}
              validation={validation}
              materials={materials}
              onValidate={handleValidate}
              validating={validating[validation._id]}
              onChatClick={handleChatClick}
              currentAgentId={user?._id}
            />
          ))}
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal
        binId={chatBinId}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />
    </div>
  );
};

// Validation Card Component
const ValidationCard = ({
  validation,
  materials,
  onValidate,
  validating,
  fetchData,
  onChatClick,
  currentAgentId,
}) => {
  const [quantities, setQuantities] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [message, setMessage] = useState("");
  const [addingToBin, setAddingToBin] = useState(false);
  const [allowValidate, setAllowValidate] = useState(false);
  const [updating, setUpdating] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Initialize quantities from validation data
    const initialQuantities = {};
    validation.materials?.forEach((item) => {
      initialQuantities[item._id] = Math.max(1, item.quantity || 1);
    });
    setQuantities(initialQuantities);
    setSelectedMaterials(validation.materials_list || []);
  }, [validation]);

  const handleQuantityChange = (itemId, quantity) => {
    setQuantities((prev) => ({
      ...prev,
      [itemId]: Math.max(1, parseFloat(quantity) || 1),
    }));
  };

const handleSaveQuantityChange = async (itemId) => {
  const quantity = quantities[itemId];
  if (quantity <= 0) {
    await handleRemoveItem(itemId);
    return;
  }

  try {
    await binService.updateBinItem(itemId, quantity);
    if (window.refreshNavbarCounts) {
      window.refreshNavbarCounts();
    }
  } catch (error) {
    console.error("Error updating quantity:", error);
    setMessage("Error updating quantity");
  } finally {
    setUpdating((prev) => ({ ...prev, [itemId]: false }));
  }
};


  const handleMaterialToggle = (materialId, checked) => {
    if (checked) {
      const material = materials.find((m) => m._id === materialId);
      if (material) {
        setSelectedMaterials((prev) => [
          ...prev,
          { material_id: materialId, quantity: 1 },
        ]);
        setQuantities((prev) => ({ ...prev, [materialId]: 1 }));
      }
    } else {
      setSelectedMaterials((prev) =>
        prev.filter((item) => item.material_id !== materialId)
      );
      setQuantities((prev) => {
        const newQuantities = { ...prev };
        delete newQuantities[materialId];
        return newQuantities;
      });
    }
  };

  const handleAddToBin = async (userId, materialId, quantity) => {
    if (quantity <= 0) {
      setMessage("Quantity must be greater than 0");
      return;
    }

    setAddingToBin(true);
    setMessage("");

    try {
      await binService.addToBin({
        userId,
        materialId,
        quantity,
      });
      setMessage("Material added to bin successfully!");
      setTimeout(() => {
        // navigate("/bin");
        fetchData();
      }, 1500);
    } catch (error) {
      console.error("Error adding to bin:", error);
      setMessage(
        error.response?.data?.message || "Error adding material to bin"
      );
    } finally {
      setAddingToBin(false);
    }
  };

  const handleSubmit = () => {
    const validationData = {
      materials: validation.materials
        .map((item) => ({
          material_id: item.material._id,
          quantity: quantities[item._id] || 0,
        })),
    };
    onValidate(validation._id, validationData);
  };

  const totalQuantity = Object.values(quantities).reduce(
    (sum, qty) => sum + (qty || 0),
    0
  );
  const totalValue = validation.materials.reduce((sum, item) => {
    const quantity = quantities[item._id] || 0;
    return sum + (item.material?.pricePerKg || 0) * quantity;
  }, 0);

  const handleRemoveItem = async (itemId) => {
    setMessage("");

    // Check if this is the last material
    if (validation.materials.length === 1) {
      setMaterialToDelete(itemId);
      setShowCancelModal(true);
      return;
    }

    try {
      await agentService.deleteBinItem(validation._id, itemId);
      setMessage("Material deleted successfully. You can add it back from the extra materials section.");
      // Refresh data
      await fetchData();
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage(error.response?.data?.message || "Error deleting material");
    }
  };

  const handleConfirmCancel = async () => {
    try {
      await agentService.cancelSellerOrder(validation._id);
      setMessage("Seller order cancelled successfully. Redirecting to pending validations...");
      setShowCancelModal(false);
      setMaterialToDelete(null);
      
      // Refresh navbar counts
      if (window.refreshNavbarCounts) {
        window.refreshNavbarCounts();
      }
      
      // Redirect to pending validations list after a short delay
      setTimeout(() => {
        window.location.href = "/agent/pending-validations";
      }, 2000);
    } catch (error) {
      console.error("Error cancelling order:", error);
      setMessage(error.response?.data?.message || "Error cancelling order");
    }
  };

  const handleCancelModal = () => {
    setShowCancelModal(false);
    setMaterialToDelete(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Bin Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Bin #{validation._id?.slice(-10) || "N/A"}
          </h3>
          <p className="text-sm text-gray-600">
            created on: {new Date(validation.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">
            Seller: {validation.user.username}
          </p>
          <p className="text-sm text-gray-600">
            {validation.materials?.length || 0} materials
          </p>
        </div>
      </div>

      {/* Seller Information */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Seller Information</h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowRedirectModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Redirect order to another agent"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Redirect Order
            </button>
            <CallButton
              calleeId={validation.user._id}
              binId={validation._id}
              calleeName={`${validation.user.firstName || ""} ${validation.user.lastName || ""}`.trim() || validation.user.username}
              variant="primary"
              className="!px-3 !py-2 !text-sm"
            />
            <StartMeetingButton
              participantId={validation.user._id}
              participantName={`${validation.user.firstName || ""} ${validation.user.lastName || ""}`.trim() || validation.user.username}
            />
            <button
              onClick={() => onChatClick(validation._id)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              title="Chat with seller"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
              Chat
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center">
            <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
            <span>
              {validation.user.firstName} {validation.user.lastName}
            </span>
          </div>
          <div className="flex items-center">
            <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
            <span>{validation.user.phoneNumber || "Not provided"}</span>
          </div>
          <div className="flex items-center md:col-span-2">
            <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
            <span>
              {[validation.user.homeAddress, validation.user.state].filter(Boolean).join(', ') || "Address not provided"}
            </span>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* View Details Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
        >
          <span className="font-medium text-gray-900">
            {showDetails ? "Hide Details" : "View Details"}
          </span>
          {showDetails ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
      </div>

      {/* Collapsible Details Section */}
      {showDetails && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Materials to Validate Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">
              Materials to Validate
            </h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {validation.materials.map((m) => {
                return (
                  <div
                    key={m._id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-gray-900 truncate">
                        {m.material.name}
                      </h5>
                      <p className="text-sm text-gray-600">
                        {m.material.category} • ₦
                        {m.material.pricePerKg?.toFixed(2) || "0.00"}/kg
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          step="0.1"
                          value={quantities[m._id] ?? m.quantity ?? 1}
                          onChange={async (e) => {
                            const newQuantity = parseFloat(e.target.value) || 1;
                            
                            if (newQuantity < 1) {
                              setMessage("Quantity cannot be less than 1");
                              return;
                            }
                            
                            handleQuantityChange(m._id, newQuantity);
                            
                            try {
                              await agentService.updateBinItemQuantity(
                                validation._id,
                                m._id,
                                newQuantity
                              );
                              setMessage("Quantity updated successfully");
                              await fetchData();
                            } catch (error) {
                              console.error("Error updating quantity:", error);
                              setMessage(error.response?.data?.message || "Error updating quantity");
                            }
                          }}
                          className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Qty"
                        />
                        <span className="text-sm text-gray-600">kg</span>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(m._id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Delete material"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Total Quantity: {totalQuantity.toFixed(1)}kg
                  </p>
                  <p className="text-sm text-gray-600">
                    {validation.materials.length} materials
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-lg font-semibold text-green-600">
                    ₦{totalValue.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600">Total Value</p>
                </div>
              </div>
            </div>

            {/* Validate Button */}
            <button
              onClick={handleSubmit}
              disabled={validating || allowValidate}
              className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              {validating ? "Validating..." : "Validate Bin"}
            </button>
          </div>

          {/* Add Extra Materials Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">
              Add Extra Materials To User Bin
            </h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {materials
                .filter(material => 
                  !validation.materials.some(binMaterial => 
                    binMaterial.material._id.toString() === material._id.toString()
                  )
                )
                .map((material) => {
                  const isSelected = selectedMaterials.some(
                    (item) => item.material_id === material._id
                  );
                  const quantity = quantities[material._id] || 0;

                  return (
                    <div
                      key={material._id}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            handleMaterialToggle(material._id, e.target.checked)
                          }
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate">
                            {material.name}
                          </h5>
                          <p className="text-sm text-gray-600">
                            {material.category} • ₦
                            {material.pricePerKg?.toFixed(2) || "0.00"}/kg
                          </p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-2 sm:ml-auto">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={quantity}
                              onChange={(e) =>
                                handleQuantityChange(material._id, e.target.value)
                              }
                              className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="Qty"
                            />
                            <span className="text-sm text-gray-600">kg</span>
                          </div>
                          <button
                            onClick={() =>
                              handleAddToBin(
                                validation.user._id,
                                material._id,
                                quantity
                              )
                            }
                            disabled={validating || selectedMaterials.length === 0}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-1.5 px-4 rounded-md text-sm transition-colors whitespace-nowrap"
                          >
                            {validating ? "Adding..." : "Add"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Cancel Seller Order
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You are about to cancel this seller's order. This action cannot be undone. 
                  The seller will be notified and can create a new order anytime they have materials to sell.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleConfirmCancel}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Confirm
                </button>
                <button
                  onClick={handleCancelModal}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Redirect Order Modal */}
      <RedirectOrderModal
        isOpen={showRedirectModal}
        onClose={() => setShowRedirectModal(false)}
        binId={validation._id}
        currentAgentId={currentAgentId}
        onRedirectSuccess={async () => {
          await fetchData();
          if (window.refreshNavbarCounts) {
            window.refreshNavbarCounts();
          }
        }}
      />
    </div>
  );
};

export default PendingValidations;
